import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next";
import { Select } from 'antd';
import modalStyles from '../../../components/modalWindows/styles/BaseModalWindow.module.scss';
import { TextInput, NumberInput, TextAreaInput, DateInput } from "../../../components/controls/inputs";
import GridWrapper from "../../../components/controls/GridWrapper";
import FlexColumn from "../../../components/controls/FlexColumn";
import BaseCreator from "../../../components/creators/baseCreator";
import { useForm } from "../../../system/hooks/useForm";
import { useNewsMaker } from "./useNewsMaker";
import styles from './styles/NewsMaker.module.scss'
import { INewsMakerItemCreateDTO, INewsMakerItemEditDTO, RedmineNewsPriority } from "../../../libs/coreapi-dto/service/newsChat";
import { DateTime } from "luxon";
import { useUserContext } from "../../../system/providers/userContextProvider";

interface INewsCreatorModalProps {
    variant: 'create' | 'edit';
    data?: INewsMakerItemEditDTO | INewsMakerItemCreateDTO;
    save: (data: INewsMakerItemEditDTO | INewsMakerItemCreateDTO) => void;
    cancel: () => void;
    lockFromPermission?: boolean;
}

interface IValidator {
    subject: { required?: { value: boolean; message: string } };
    description: { required?: { value: boolean; message: string } };
    startDate: { required?: { value: boolean; message: string } };
    dueDate: { required?: { value: boolean; message: string } };
    estimatedHour: { required?: { value: boolean; message: string } };
    priority: { required?: { value: boolean; message: string } };
    datesRange: { custom?: { isValid: () => boolean; message: string } };
}
const NewsMakerCreatorModal: FC<INewsCreatorModalProps> = (props) => {
    const isEdit = props.variant === 'edit';
    const getDefaultNewsData = (): INewsMakerItemCreateDTO => ({
        id: 0,
        subject: '',
        description: '',
        startDate: null,
        dueDate: null,
        estimatedHour: 0,
        priority: 0,
    });
    const userCtx = useUserContext();

    const getInitialData = (): INewsMakerItemCreateDTO => {
        if (isEdit && props.data) {
            const editData = props.data as INewsMakerItemEditDTO;
            return {
                id: editData.id,
                subject: editData.subject,
                description: editData.description,
                startDate: editData.startDate,
                dueDate: editData.dueDate,
                estimatedHour: editData.estimatedHour,
                priority: editData.priority,
            };
        }
        if (props.data && !isEdit) {
            return props.data as INewsMakerItemCreateDTO;
        }
        return getDefaultNewsData();
    };

    const {
        news,
        updateSubject,
        updateDescription,
        updateStartDate,
        updateDueDate,
        updateEstimatedHour,
        updatePriority,
    } = useNewsMaker(getInitialData());

    const { t } = useTranslation();
    const baseT = (value: string) => t('directory.newsMaker.' + value);
    const errorsT = (value: string) => t('errors.' + value);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const priorityOptions = [
        { value: RedmineNewsPriority.Low, label: t('directory.newsMaker.priority.low') },
        { value: RedmineNewsPriority.Medium, label: t('directory.newsMaker.priority.medium') },
        { value: RedmineNewsPriority.High, label: t('directory.newsMaker.priority.high') },
        { value: RedmineNewsPriority.Critical, label: t('directory.newsMaker.priority.critical') },
    ];

  
    const validateDates = useCallback((): { isValid: boolean; errorKey: string | null } => {
        // Проверка диапазона дат (только если обе даты указаны)
        if (news.startDate && news.dueDate && news.startDate > news.dueDate) {
            return { isValid: false, errorKey: 'startDateAfterDueDate' };
        }
        
        // Проверка, что dueDate не в прошлом
        if (news.dueDate) {
            const today = DateTime.now().startOf('day');
            if (news.dueDate < today) {
                return { isValid: false, errorKey: 'dueDateCannotBeInPast' };
            }
        }
        
        return { isValid: true, errorKey: null };
    }, [news.startDate, news.dueDate]);

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            subject: {
                required: {
                    value: !news.subject,
                    message: errorsT("required")
                }
            },
            description: {
                required: {
                    value: !news.description,
                    message: errorsT("required")
                }
            },
           
            estimatedHour: {
                required: {
                    value: news.estimatedHour === undefined || news.estimatedHour === null,
                    message: errorsT("required")
                }
            },
            priority: {
                required: {
                    value: news.priority === undefined || news.priority === null,
                    message: errorsT("required")
                }
            },
            datesRange: {
                custom: {
                    isValid: () => validateDates().isValid,
                    message: baseT(validateDates().errorKey || 'startDateAfterDueDate')
                }
            }
        }
    });

    useEffect(() => {
        if (errors.datesRange) {
            const { isValid: isDatesValid } = validateDates();
            if (isDatesValid) {
                setErrors(prev => ({ ...prev, datesRange: undefined }));
            } else {
                // Обновляем сообщение об ошибке, если оно изменилось
                const errorKey = validateDates().errorKey;
                if (errorKey) {
                    setErrors(prev => ({ ...prev, datesRange: baseT(errorKey) }));
                }
            }
        }
    }, [news.startDate, news.dueDate, validateDates]);

    const handleStartDateChange = (date: DateTime | null | undefined) => {
        if (date === undefined || date === null) return;
        updateStartDate(date.toF3FixedZoneNow());
        
        // Очищаем ошибку дат при изменении
        if (errors.datesRange) {
            setErrors(prev => ({ ...prev, datesRange: undefined }));
        }
    };

    const handleDueDateChange = (date: DateTime | null | undefined) => {
        if (date === undefined || date === null) return;

        const dateWithEndTime = date.endOf('day');

        updateDueDate(dateWithEndTime.toF3DateTimeServerSide(userCtx));
        
        // Очищаем ошибку дат при изменении
        if (errors.datesRange) {
            setErrors(prev => ({ ...prev, datesRange: undefined }));
        }
    };

    const handleSubmit = () => {
        // Принудительно проверяем валидность дат перед отправкой
        const { isValid: areDatesValid } = validateDates();
        
        if (!areDatesValid) {
            const errorKey = validateDates().errorKey;
            setErrors(prev => ({ 
                ...prev, 
                datesRange: baseT(errorKey || 'startDateAfterDueDate') 
            }));
            return;
        }
        
        const isFormValid = isValid();
        
        if (isFormValid && areDatesValid) {
            setIsSubmitting(true);
            
            if (isEdit) {
                const editData: INewsMakerItemEditDTO = {
                    ...news,
                    idGlobal: (props.data as INewsMakerItemEditDTO).idGlobal,
                };
                props.save(editData);
            } else {
                props.save(news);
            }
        } else {
            console.log(baseT('validationFailed'));
        }
    };

    return (
        <BaseCreator
            variant={props.variant}
            isSubmitting={isSubmitting}
            save={handleSubmit}
            modalWindowClassName={modalStyles.flexModalWindow}
            cancel={props.cancel}
            valid={!props.lockFromPermission}
        >
            <div className={styles.modalContentWindow}>
                <FlexColumn>
                    <GridWrapper cols={1}>
                        <TextInput
                            required
                            error={errors.subject}
                            label={baseT('subject')}
                            value={news.subject}
                            onChange={updateSubject}
                            placeholder={baseT('subjectPlaceholder')}
                        />
                    </GridWrapper>
                    <GridWrapper cols={1}>
                        <TextAreaInput
                            required
                            error={errors.description}
                            label={baseT('description')}
                            value={news.description}
                            onChange={updateDescription}
                            placeholder={baseT('descriptionPlaceholder')}
                            largeTextArea
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <DateInput
                            labelVariant="required"
                            inputId={"inputStartDate"}
                            label={baseT('startDate')}
                            value={news.startDate}
                            onBlur={(date) => {
                                handleStartDateChange(date);
                            }}
                        />
                        <DateInput
                            labelVariant="required"
                            inputId={"inputInvoiceDate"}
                            label={baseT('dueDate')}
                            value={news.dueDate}
                            onBlur={(date) => {
                                handleDueDateChange(date);
                            }}
                        />
                    </GridWrapper>
                    {errors.datesRange && (
                        <div className={styles.errorMessage} style={{ color: 'red', fontSize: '12px', marginTop: '-10px', marginBottom: '10px' }}>
                            {errors.datesRange}
                        </div>
                    )}
                    <GridWrapper cols={2}>
                        <NumberInput
                            required
                            inline
                            label={baseT('estimatedHour')}
                            value={news.estimatedHour}
                            error={errors.estimatedHour}
                            onChange={updateEstimatedHour}
                            min={0}
                            placeholder={baseT('estimatedHourPlaceholder')}
                        />
                        <div className={styles.fieldWrapper}>
                            <div className={styles.labelWrapper}>
                                <span>{baseT('priorityTitle')}</span>
                            </div>
                            <Select
                                value={news.priority}
                                onChange={updatePriority}
                                options={priorityOptions}
                                placeholder={baseT('priorityPlaceholder')}
                                style={{ width: '100%' }}
                                status={errors.priority ? 'error' : undefined}
                            />
                        </div>
                    </GridWrapper>
                </FlexColumn>
            </div>
        </BaseCreator>
    )
}

export default NewsMakerCreatorModal;